import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usersApi, PublicUser } from '../api/users';
import PlayedEvents from '../components/PlayedEvents';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_API_URL ?? '';

const CLASS_STYLE: Record<string, string> = {
  A: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  B: 'bg-blue-100 text-blue-800 border-blue-300',
  C: 'bg-gray-100 text-gray-600 border-gray-300',
};

function eloClass(elo: number): 'A' | 'B' | 'C' {
  if (elo >= 1150) return 'A';
  if (elo >= 950) return 'B';
  return 'C';
}

export default function UserPublicProfile() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { data: user, isLoading, isError } = useQuery<PublicUser>({
    queryKey: ['user-public', id],
    queryFn: () => usersApi.getPublic(id!).then((r) => r.data),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12">
        <div className="card animate-pulse space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gray-200" />
            <div className="space-y-2 flex-1">
              <div className="h-5 bg-gray-200 rounded w-1/2" />
              <div className="h-4 bg-gray-100 rounded w-1/3" />
            </div>
          </div>
          <div className="h-16 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">{t('profile.notFound', 'Spieler nicht gefunden')}</p>
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm mt-4 inline-block">
          ← {t('nav.home', 'Startseite')}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="card space-y-5">
        {/* Avatar + Name */}
        <div className="flex items-center gap-4">
          {user.photo_url ? (
            <img
              src={`${API_URL}${user.photo_url}`}
              alt={`${user.first_name} ${user.last_name}`}
              className="w-20 h-20 rounded-full object-cover border-2 border-primary-100"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-2xl font-bold border-2 border-primary-200">
              {user.first_name[0]}{user.last_name[0]}
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {user.first_name} {user.last_name}
            </h1>
            {user.level && (
              <span className="badge bg-primary-100 text-primary-700 mt-1">{user.level}</span>
            )}
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-sm font-bold text-gray-900">ELO {user.elo}</span>
              <span className={`badge border text-xs ${CLASS_STYLE[eloClass(user.elo)]}`}>
                {eloClass(user.elo)}-Klasse
              </span>
            </div>
          </div>
        </div>

        {/* Über mich */}
        {user.about_me && (
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">{t('profile.aboutMe')}</p>
            <p className="text-sm text-gray-700 whitespace-pre-line">{user.about_me}</p>
          </div>
        )}
      </div>

      <PlayedEvents userId={id!} />
    </div>
  );
}
